import { Inject, Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import {
  CsHttpClientService,
  JwtService,
  LOGIN_ROUTE,
  NO_ACCESS,
  StorageService
} from '@cbuild/cs-common-data-services';
import { BehaviorSubject, map, Observable, ReplaySubject, take } from 'rxjs';
import { OrganizationConfigurationEnum } from '../../../shared/enums/organization-configurations-enum';
import { SoftPhoneService } from '../../../shared/soft-phone.service';
import { BaseUrlService } from '../base-url.service';
import { FirebaseMessagingService } from '../firebase-messaging.service';

export interface User {
  exp: number;
  iss: string;
  aud: string;
  uid: string;
  useremail: string;
  csp: string;
  csm: string;
  usp: string;
  usc: string;
  rf: string;
  vars: string;
  theme: string;
  type: string;
  [key: string]: string | number | null;
}

const USER_TOKEN: string = 'l_token';
const SELECTED_ORG: string = 's_org';
const Calling_Type: string = 'mobile';
const Soft_Phone_Email: string = 'soft_phone_email';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  public _redirectURL: UrlTree | undefined;
  private _isAuthenticated = false;
  private _apiKey: string | null = null;
  public user$ = new ReplaySubject<User | null>(1);
  public $token = new BehaviorSubject<null | {
    token: string | null;
    apiKey: string | null;
  }>(null);
  private _user: User | null;
  public $orgId = new ReplaySubject<string | null>(1);
  private systems: Array<string>;
  private _token: string | null;
  private _isOrgSelected = false;
  private _orgId: string | null;
  private _orgSelectRoute = '/select-org';
  private _hasMultipleOrg = false;
  public $hasMultipleOrg = new ReplaySubject<boolean>(1);
  public orgConfigEnum = OrganizationConfigurationEnum;

  constructor(
    private router: Router,
    private storageService: StorageService,
    private jwtService: JwtService,
    @Inject(LOGIN_ROUTE) private loginRoute: string,
    @Inject(NO_ACCESS) private noAccessPage: string,
    private http: CsHttpClientService,
    private baseUrlService: BaseUrlService,
    private messagingService: FirebaseMessagingService,
    private softPhoneService: SoftPhoneService
  ) {}

  public async initialize(): Promise<void> {
    const token = this.storageService.getItem<string>(USER_TOKEN);
    this._orgId = this.storageService.getItem<string>(SELECTED_ORG);
    this.updateAuthentication(token);
    if (this.isUserExpired()) {
      await new Promise(resolve => {
        this.refreshToken()
          .pipe(take(1))
          .subscribe(() => {
            resolve(0);
          });
      });
    }
    this.listenForToken();
    this.listenForOrganization();
    const email = this.getSoftPhoneEmail();
    if (email != null && email != '') {
      this.softPhoneService.openPopup(email);
    }
  }

  public get authInitialized(): Observable<boolean> {
    return this.user$.pipe(map(() => this.isAuthenticated));
  }

  /**
   * sets jwt token
   * @param token jwt token
   */
  public setJWTToken(token: string): void {
    this.storageService.setItem(USER_TOKEN, token);
  }

  /**
   * Gets Current User Data
   */
  public getUser(): User | null {
    return this._user;
  }

  /**
   * Gets Current org
   */
  public get orgId(): string | null {
    return this._orgId;
  }

  /**
   * Clears LocalStorage Token value
   */
  public logOut(navigateToLogin = true): void {
    const rf = this._user ? this._user.rf : '';
    this.deleteFcmToken(this._apiKey);
    this.deleteRefreshToken(rf);
    this._isAuthenticated = false;
    this.storageService.removeItem(USER_TOKEN);
    this.storageService.removeItem(SELECTED_ORG);
    this.clearSoftPhoneEnabled();
    this.clearSoftPhoneEmail();
    if (navigateToLogin) {
      this.navigateToLogin();
    }
  }

  private deleteFcmToken(apiKey: string | null): void {
    if (apiKey) {
      this.messagingService
        .deleteFcmToken(apiKey)
        .pipe(take(1))
        .subscribe(() => {
          this.storageService.removeItem('fcmToken');
        });
    }
  }

  /**
   * Checks if user is authenticated
   */
  public get isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  /**
   * Checks if org is selected
   */
  public get isOrgSelected(): boolean {
    return this._isOrgSelected;
  }

  /**
   * gets jwt token
   */
  public getToken(): string | null {
    return this._token;
  }

  /**
   * Checks if JWT Token Expired
   *  @param threshold Threshold in `seconds`
   */
  public isUserExpired(threshold = 5): boolean {
    if (!this._user || !this._user.exp) {
      return false;
    }
    const instant = Math.floor(new Date().getTime() / 1000);
    return this._user.exp < instant + threshold;
  }

  /**
   * Checks if User has permission code
   * @param appCode Permission Code
   * @param requiredPermissionMask permission mask
   */
  public hasUserPermission(
    appCode: string,
    requiredPermissionMask: number
  ): boolean {
    // if the required permission is 0, it is free!
    if (requiredPermissionMask === 0) {
      return true;
    }

    // the scope claim is a space separated list like: "FMGT:255"  etc
    const lookupString: string = appCode.toUpperCase() + ':';
    const neededSystem: string | undefined = this.systems.find(i =>
      i.toUpperCase().startsWith(lookupString)
    );
    if (!neededSystem) {
      return false;
    }

    // now look up the required permission against the number
    const userPermissions: number = parseInt(
      neededSystem.replace(lookupString, '').trim(),
      10
    );
    if (userPermissions) {
      return (userPermissions & requiredPermissionMask) > 0;
    }
    return false;
  }

  /**
   * Checks if module `permission` in token
   * @param permission module permission code
   */
  public hasModulePermission(permission: string): boolean {
    return !!this._user?.csm.includes(permission.toUpperCase());
  }

  public hasFeaturePermission(permission: string): boolean {
    return !!this._user?.usp.includes(permission.toUpperCase());
  }

  /**
   * Checks if product `permission` in token
   * @param permission product permission code
   */
  public hasProductPermission(permission: string): boolean {
    return !!this._user?.csp.includes(permission.toUpperCase());
  }

  public navigateToLogin(): void {
    this.router.navigate([this.loginRoute]);
  }

  public navigateToSelectOrg(): void {
    this.router.navigate([this._orgSelectRoute]);
  }

  public navigateToHomePage(): void {
    const homePage = this.baseUrlService.getHomeBaseURL();
    this.router.navigate([homePage]);
  }

  public navigateToNoAccess(): void {
    this.router.navigate([this.noAccessPage]);
  }

  /**
   * Parses and updates the JWT Token
   * @param jwt jwt token
   */
  private updateAuthentication(jwt: string): void {
    let obj: User | null = null;
    try {
      if (jwt) {
        obj = this.jwtService.decodeToken(jwt) as User;
        if (obj) {
          this.validateToken(obj);
          this.parseTokenPayload(obj);
          this._isOrgSelected = this._orgId ? true : false;
          this.setOrgIfUserHasOne(obj.vars);
          const homeUrl = this.parseHomeUrl(obj.vars);
          this.baseUrlService.setHomeBaseUrl(homeUrl);
        }
      }
    } catch (e) {
      obj = null;
    } finally {
      this._isAuthenticated = !!obj?.uid;
      this._token = this._isAuthenticated ? jwt : null;
      this.setToken(obj?.vars, this._orgId, this._token);
      this.$orgId.next(this._orgId);
      this.setUser(obj);
    }
  }

  private setToken(
    vars: string | undefined,
    orgId: string | null,
    token: string | null
  ): void {
    if (!this._isAuthenticated) {
      this.$token.next(null);
      return;
    }
    if (this._isOrgSelected) {
      const lookUpString = `${orgId}:CNKY:`;
      const reqVar = vars?.split(',').find(x => x.startsWith(lookUpString));
      this._apiKey = reqVar ? reqVar.replace(lookUpString, '') : null;
    }
    this.$token.next({ token, apiKey: this._apiKey });
  }

  private setUser(_user: User | null): void {
    this._user = _user;
    this.user$.next(_user);
  }

  private setOrgIfUserHasOne(vars: string): void {
    const allVars = vars.split(',');
    const conVars = allVars.filter(x => x.includes('CNKY'));
    this._hasMultipleOrg = conVars.length > 1;
    this.$hasMultipleOrg.next(this._hasMultipleOrg);
    if (this._isOrgSelected || conVars.length > 1) {
      return;
    }
    this._orgId = conVars[0].split(':')[0];
    this._isOrgSelected = this._orgId ? true : false;
    this.storageService.setItem(SELECTED_ORG, this._orgId);
  }

  public setOrgId(orgId: string): void {
    this._orgId = orgId;
    this.storageService.setItem(SELECTED_ORG, orgId);
  }

  /**
   * Active Token change listener on `LocalStorageService`
   */
  private listenForToken(): void {
    this.storageService.getItem$<string>(USER_TOKEN).subscribe(token => {
      this.updateAuthentication(token);
    });
  }

  /**
   * Active Token change listener on `LocalStorageService`
   */
  private listenForOrganization(): void {
    this.storageService.getItem$<string>(SELECTED_ORG).subscribe(_ => {
      this._orgId = _;
      if (this._token) {
        this.updateAuthentication(this._token);
      }
    });
  }

  /**
   * Validates is token payload has `usp` Object
   * @param tokenObj token Payload
   */
  private validateToken(tokenObj: User): void {
    if (!tokenObj.usp) {
      throw new Error('CSuite Token no permissions in token');
    }
  }

  /**
   * Parses toke `usp`
   * @param tokenObj token Payload
   */
  private parseTokenPayload(tokenObj: User): void {
    this.systems = tokenObj.usp.split(',');
    if (this.systems.length === 0) {
      throw new Error('CSuite Token no application codes specified in token');
    }
  }

  private parseHomeUrl(vars: string): string {
    if (this._isOrgSelected) {
      const lookUpString = `${this._orgId}:HURI:`;
      const reqVar = vars.split(',').find(x => x.startsWith(lookUpString));
      return reqVar ? reqVar.replace(lookUpString, '') : '/';
    }
    return this._orgSelectRoute;
  }

  public refreshToken(): Observable<void> {
    if (!this._user?.ref) {
      return this.http
        .writeText<{ token: string }>(
          'api/master/refreshToken',
          this._user!.rf,
          this.baseUrlService.getIdentityBaseURL()
        )
        .pipe(
          map(_ => {
            this.setJWTToken(_.token);
          })
        );
    }
    throw new Error('Refresh token not found');
  }

  public deleteRefreshToken(rf: string): void {
    if (this.isAuthenticated) {
      const sub = this.http
        .writeText<string>(
          'api/account/user/access-logout',
          rf,
          this.baseUrlService.getIdentityBaseURL()
        )
        .pipe(take(1))
        .subscribe(_ => {
          sub.unsubscribe();
        });
    }
  }

  public setSoftPhoneEnabled(callingType: string): void {
    // Save it as a string to ensure predictable retrieval
    this.storageService.setItem(Calling_Type, callingType);
  }

  public isSoftPhoneEnabled(): string {
    return this.storageService.getItem<string>(Calling_Type);
  }

  public clearSoftPhoneEnabled(): void {
    this.storageService.removeItem(Calling_Type);
  }

  public setSoftPhoneEmail(email: string): void {
    // Save it as a string to ensure predictable retrieval
    this.storageService.setItem(Soft_Phone_Email, email);
  }

  public getSoftPhoneEmail(): string {
    return this.storageService.getItem<string>(Soft_Phone_Email);
  }

  public clearSoftPhoneEmail(): void {
    this.storageService.removeItem(Soft_Phone_Email);
  }
}
